import React, { useEffect } from 'react';
import { startSessionReplay, stopSessionReplay, useIdentifyUser } from 'lib.analytics';
import { useAuth } from 'lib.context/src/AuthContext';
import { ORGANIZATION_TYPE } from 'lib.store/src/main/organization/organizationType';

/** The accounts whose sessions are recorded — what the admin's User Sessions list shows. */
const RECORDED_TYPES: ReadonlyArray<string> = [
  ORGANIZATION_TYPE.partner,
  ORGANIZATION_TYPE.organizer,
];

/**
 * Binds product analytics to the signed-in account, and records the session of
 * a sponsor or an organizer for as long as they are signed in — the admin's
 * user page lists those recordings (inputs stay masked, see the client).
 *
 * An admin inside someone's account through an avatar is neither: the browser
 * is the admin's, so it is not tagged with the sponsor's id and nothing is
 * recorded — otherwise the admin's clicks would read as the sponsor's
 * sessions, and the sponsor's id would stick to the admin's browser.
 *
 * Lives in the app rather than in lib.analytics so the analytics package stays
 * free of any dependency on how this app happens to store its session — the
 * package takes ids, the app knows where they come from.
 */
const AnalyticsIdentityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const isAvatar = Boolean(user?.is_avatar);

  useIdentifyUser({
    userId: isAvatar ? undefined : user?.id,
    organizationId: user?.organization?.id,
    organizationName: user?.organization?.name,
    organizationType: user?.organization?.type,
  });

  const isRecorded = !isAvatar && RECORDED_TYPES.includes(user?.organization?.type ?? '');
  useEffect(() => {
    if (!isRecorded) return undefined;
    startSessionReplay();
    return () => stopSessionReplay();
  }, [isRecorded]);

  return <>{children}</>;
};

export default AnalyticsIdentityProvider;
